<template>
  <div class="flex flex-wrap">
    <div class="w-full py-2">
      <div>
        <label class="uppercase font-semibold text-sm">
          Owner <span class="text-red-500 ml-1">*</span>
        </label>

        <div>
          <ContactAutocomplete
              class="w-full"
              v-model="owner"
              @contactSelected="handleOwnerSelected"
              @contactCleared="handleOwnerCleared"
              :forceSelection="true"
              :disabled="!!customer"
          />
        </div>

        <div class="w-full flex">
          <ContactInfoCard v-if="owner" :contact="owner"></ContactInfoCard>
        </div>
      </div>
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm flex items-center">
        Product <span class="text-red-500 ml-1">*</span>
        <SearchTipsInfo class="ml-2" />
      </label>
      <div>
        <ProductAutoComplete
            class="w-full"
            v-model="selectedProduct"
            :showTips="false"
            @productSelected="handleProductSelected"
            @productCleared="handleProductCleared"
        />
      </div>
    </div>

    <div v-if="selectedProductInfo" class="w-full py-2">
      <ProductInfoCard :product="selectedProductInfo" />
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">
        Serial Number
        <span v-if="isSerialNumberRequired" class="text-red-500">*</span>
      </label>
      <input
          v-model="equipmentDetails.serial_number"
          class="w-full bge-input bge-input-spacing rounded"
          type="text"
          :placeholder="isSerialNumberRequired ? 'Enter Serial Number' : 'Optional'"
      />
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Location</label>
      <select v-model="equipmentDetails.location" class="w-full bge-input bge-select rounded">
        <option v-for="loc in equipmentLocations" :key="loc" :value="loc">{{ loc }}</option>
      </select>
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">
        Managed By Us <span class="text-red-500">*</span>
      </label>
      <div>
        <ToggleButton
            v-model="equipmentDetails.is_managed"
            onLabel="YES"
            offLabel="NO"
            style="width: 10em"
            @change="handleManagedChange"
        />
      </div>
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Service Agent</label>
      <ContactAutocomplete
          class="w-full"
          v-model="serviceAgent"
          :forceSelection="true"
      />
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Seller Agent</label>
      <ContactAutocomplete
          class="w-full"
          v-model="sellerAgent"
          :forceSelection="true"
      />
    </div>

    <div class="flex w-full justify-end mt-4">
      <AppButton
          color="blue"
          label="Save"
          @click="saveEquipment"
          :icon="true"
          class="mr-2"
      >
        <template v-slot:icon>
          <Icon iconType="save" class="mr-2" />
        </template>
      </AppButton>
    </div>
  </div>
</template>

<script>
import ContactInfoCard from "@/components/Contacts/InfoCard.vue";
import ProductInfoCard from "@/components/Products/InfoCard.vue";
import SearchTipsInfo from "@/components/AutoComplete/SearchTipsInfo.vue";
import ContactAutocomplete from "@/components/AutoComplete/ContactAutoComplete.vue";
import ProductAutoComplete from "@/components/AutoComplete/ProductAutoComplete.vue";
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import { BOSTON_SOUTH_WEST_CONTACT } from "@/constants/company";

export default {
  name: "AddEquipment",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    ContactAutocomplete,
    ProductAutoComplete,
    ContactInfoCard,
    ProductInfoCard,
    SearchTipsInfo,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isBusy: false,
      owner: null,
      selectedProduct: null,
      serviceAgent: null,
      sellerAgent: null,
      ownerDistributor: null,
      // Equipment is added against an owner, so it starts at the customer's site
      equipmentLocations: ["Customer", "Agent", "Workshop", "Stock"],
      equipmentDetails: {
        owner_id: "",
        product_id: "",
        product_code: "",
        name: "",
        product_type: "",
        description: "",
        serial_number: "",
        location: "Customer",
        is_managed: false,
      },
    };
  },
  computed: {
    isSerialNumberRequired() {
      const normalizedType = (this.equipmentDetails.product_type || "").trim().toUpperCase();
      return normalizedType === "EQUIPMENT WITH SERIAL NUMBER";
    },
    selectedProductInfo() {
      return this.selectedProduct && this.selectedProduct.value
          ? this.selectedProduct.value
          : null;
    },
  },
  mounted() {
    this.applyCustomer(this.customer);
  },
  watch: {
    customer: {
      immediate: true,
      handler(newCustomer) {
        this.applyCustomer(newCustomer);
      },
    },
  },
  methods: {
    applyCustomer(customer) {
      if (!customer || typeof customer !== "object") return;

      this.handleOwnerSelected(customer);
    },

    handleOwnerSelected(selectedCustomer) {
      const rawContact = selectedCustomer && selectedCustomer.value
          ? selectedCustomer.value
          : selectedCustomer;

      if (!rawContact || typeof rawContact !== "object") {
        this.handleOwnerCleared();
        return;
      }

      // Customer's default distributor (Accounts tab) -> default service and
      // seller agent, unless Managed By Us = YES (Boston SW wins then).
      this.ownerDistributor = rawContact.agent_id
          ? { contact_id: rawContact.agent_id, name: rawContact.agent_name || "" }
          : null;
      if (!this.equipmentDetails.is_managed) {
        this.serviceAgent = this.ownerDistributor ? { ...this.ownerDistributor } : null;
        this.sellerAgent = this.ownerDistributor ? { ...this.ownerDistributor } : null;
      }

      if (this.owner && this.owner.address && this.owner.people) {
        this.equipmentDetails.owner_id = this.owner.contact_id || rawContact.contact_id || "";
        return;
      }

      const addresses = rawContact.addresses;
      const peopleList = rawContact.people;

      let addressToAssign = null;
      if (addresses && Array.isArray(addresses)) {
        const defaultAddress = addresses.find(function (addr) {
          return addr.is_default;
        });
        addressToAssign = defaultAddress || addresses[0];
      } else if (rawContact.address) {
        addressToAssign = rawContact.address;
      }

      let personToAssign = null;
      if (peopleList && Array.isArray(peopleList)) {
        const defaultPerson = peopleList.find(function (person) {
          return person.is_default;
        });
        personToAssign = defaultPerson || peopleList[0];
      } else if (rawContact.people && !Array.isArray(rawContact.people)) {
        personToAssign = rawContact.people;
      }

      this.owner = {
        ...rawContact,
        address: addressToAssign,
        people: personToAssign,
      };

      this.equipmentDetails.owner_id = rawContact.contact_id || "";
    },

    handleOwnerCleared() {
      this.owner = null;
      this.equipmentDetails.owner_id = "";
      this.ownerDistributor = null;
      if (!this.equipmentDetails.is_managed) {
        this.serviceAgent = null;
        this.sellerAgent = null;
      }
    },

    handleProductSelected(selectedProduct) {
      if (!selectedProduct) {
        this.handleProductCleared();
        return;
      }

      const product = selectedProduct.value ? selectedProduct.value : selectedProduct;
      const rawType = product.product_type || product.type || "";
      const normalizedType = rawType.trim().toUpperCase();

      if (
          normalizedType !== "EQUIPMENT WITH SERIAL NUMBER" &&
          normalizedType !== "EQUIPMENT WITHOUT SERIAL NUMBER"
      ) {
        this.notifyError(
            "Only products with type 'EQUIPMENT WITH SERIAL NUMBER' or 'EQUIPMENT WITHOUT SERIAL NUMBER' can be selected."
        );
        this.handleProductCleared();
        return;
      }

      this.selectedProduct = {
        label: product.name,
        product_id: product.product_id,
        product_code: product.product_code,
        name: product.name,
        value: {
          product_id: product.product_id,
          product_code: product.product_code,
          name: product.name,
          description: product.description,
          type: rawType,
          product_type: rawType,
          record_number: product.record_number || "",
          service_interval: product.service_interval || null,
          calibration_interval: product.calibration_interval || null,
          warranty_period: product.warranty_period || null,
        },
      };

      this.equipmentDetails.product_id = product.product_id || "";
      this.equipmentDetails.product_code = product.product_code || "";
      this.equipmentDetails.name = product.name || "";
      this.equipmentDetails.description = product.description || "";
      this.equipmentDetails.product_type = rawType || "";

      if (normalizedType === "EQUIPMENT WITHOUT SERIAL NUMBER") {
        this.equipmentDetails.serial_number = "";
      }
    },

    // Managed By Us = YES -> default both agents to Boston South West (still
    // editable); NO -> the owner's default distributor if they have one,
    // otherwise blank (field requests).
    handleManagedChange() {
      if (this.equipmentDetails.is_managed) {
        this.serviceAgent = { ...BOSTON_SOUTH_WEST_CONTACT };
        this.sellerAgent = { ...BOSTON_SOUTH_WEST_CONTACT };
      } else {
        this.serviceAgent = this.ownerDistributor ? { ...this.ownerDistributor } : null;
        this.sellerAgent = this.ownerDistributor ? { ...this.ownerDistributor } : null;
      }
    },

    handleProductCleared() {
      this.selectedProduct = null;
      this.equipmentDetails.product_id = "";
      this.equipmentDetails.product_code = "";
      this.equipmentDetails.name = "";
      this.equipmentDetails.description = "";
      this.equipmentDetails.product_type = "";
      this.equipmentDetails.serial_number = "";
    },

    validateBeforeSave() {
      if (!this.equipmentDetails.owner_id) {
        this.notifyError("Owner is required.");
        return false;
      }

      if (
          !this.equipmentDetails.product_id ||
          !this.equipmentDetails.product_code ||
          !this.equipmentDetails.name
      ) {
        this.notifyError("Please select a valid product.");
        return false;
      }

      const normalizedType = (this.equipmentDetails.product_type || "").trim().toUpperCase();

      if (
          normalizedType !== "EQUIPMENT WITH SERIAL NUMBER" &&
          normalizedType !== "EQUIPMENT WITHOUT SERIAL NUMBER"
      ) {
        this.notifyError("Selected product must be an equipment product.");
        return false;
      }

      if (
          normalizedType === "EQUIPMENT WITH SERIAL NUMBER" &&
          (!this.equipmentDetails.serial_number || !this.equipmentDetails.serial_number.trim())
      ) {
        this.notifyError("Serial Number is required.");
        return false;
      }

      return true;
    },

    buildPayload() {
      return {
        owner_id: this.equipmentDetails.owner_id,
        product_id: this.equipmentDetails.product_id,
        serial_number: this.equipmentDetails.serial_number
            ? this.equipmentDetails.serial_number.trim()
            : "",
        is_managed: this.equipmentDetails.is_managed === true,
        location: this.equipmentDetails.location || "Customer",
        service_agent_id:
            this.serviceAgent && this.serviceAgent.contact_id
                ? this.serviceAgent.contact_id
                : null,
        seller_agent_id:
            this.sellerAgent && this.sellerAgent.contact_id
                ? this.sellerAgent.contact_id
                : null,
      };
    },

    buildMovePayload(existingEquipment) {
      return {
        owner_id: this.equipmentDetails.owner_id,
        metadata: {
          ...(existingEquipment.metadata || {}),
          moved_equipment: {
            previous_owner_id: existingEquipment.owner_id || null,
            new_owner_id: this.equipmentDetails.owner_id,
            moved_at: Math.floor(Date.now() / 1000),
          },
        },
      };
    },

    getExistingOwnerName(existingEquipment) {
      if (existingEquipment && existingEquipment.owner && existingEquipment.owner.name) {
        return existingEquipment.owner.name;
      }
      return existingEquipment && existingEquipment.owner_id
          ? existingEquipment.owner_id
          : "another customer";
    },

    formatValidationMessage(message) {
      if (!message || typeof message !== "string") return "";

      message = message.replace(/["']/g, "");
      message = message.replace(/[_\.]/g, " ");
      message = message.replace(/\s+/g, " ").trim();
      message = message.replace(/\b\w/g, function (char) {
        return char.toUpperCase();
      });

      return message;
    },

    async checkExistingEquipmentBySerial() {
      if (!this.isSerialNumberRequired) {
        return null;
      }

      const serialNumber = this.equipmentDetails.serial_number
          ? this.equipmentDetails.serial_number.trim()
          : "";

      if (!serialNumber) {
        return null;
      }

      try {
        const existingEquipment = await this.EquipmentService.getEquipmentItemBySerialNumber(serialNumber);
        return existingEquipment || null;
      } catch (error) {
        if (error && error.status === 404) {
          return null;
        }

        throw error;
      }
    },

    async handleExistingSerialScenario(existingEquipment) {
      if (!existingEquipment || !existingEquipment.equipment_id) {
        return false;
      }

      const existingOwnerId = existingEquipment.owner_id || "";
      const newOwnerId = this.equipmentDetails.owner_id || "";

      if (existingOwnerId && existingOwnerId === newOwnerId) {
        this.notifyError("This serial number is already assigned to this customer.");
        return true;
      }

      if (
          existingEquipment.product_id &&
          this.equipmentDetails.product_id &&
          existingEquipment.product_id !== this.equipmentDetails.product_id
      ) {
        this.notifyError(
            "This serial number is already assigned to a different product. Please check the product and serial number."
        );
        return true;
      }

      const currentOwnerName = this.getExistingOwnerName(existingEquipment);
      const confirmation = await this.confirmAction({
        message:
            "This serial number is already assigned to " +
            currentOwnerName +
            ". Do you want to move this equipment to the selected customer?",
        header: "Move Equipment",
        acceptLabel: "Move Equipment",
        rejectLabel: "Cancel",
      });

      if (!confirmation) {
        return true;
      }

      const movePayload = this.buildMovePayload(existingEquipment);
      await this.EquipmentService.updateEquipment(existingEquipment.equipment_id, movePayload);

      this.notifySuccess("Equipment moved successfully");

      await this.$router.push({
        name: "equipment-detail",
        params: { equipmentId: existingEquipment.equipment_id },
      });

      return true;
    },

    async saveEquipment() {
      if (!this.validateBeforeSave()) return;

      this.isBusy = true;

      try {
        const existingEquipment = await this.checkExistingEquipmentBySerial();

        if (existingEquipment) {
          const handled = await this.handleExistingSerialScenario(existingEquipment);
          if (handled) {
            return;
          }
        }

        const payload = this.buildPayload();
        const response = await this.EquipmentService.createEquipment(payload);

        this.notifySuccess("Equipment created successfully");

        await this.$router.push({
          name: "equipment-detail",
          params: { equipmentId: response.equipment_id },
        });
      } catch (error) {
        const backendError =
            error && error.response && error.response.data
                ? error.response.data
                : error && error.data
                    ? error.data
                    : null;

        if (backendError && Array.isArray(backendError.message) && backendError.message.length > 0) {
          this.notifyError(this.formatValidationMessage(backendError.message[0]));
        } else if (backendError && backendError.message) {
          this.notifyError(backendError.message);
        } else {
          this.notifyError("Couldn't save Equipment");
        }

        console.error(backendError && backendError.message ? backendError.message : error);
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>